/**
 * Zustand store wrapping the theme module. Components subscribe to
 * `pref` to render the active radio choice; calling `setPref(p)`
 * persists, applies the new effective class to `<html>`, and
 * notifies subscribers in one step.
 *
 * Headless logic + DOM side effects live in `@/lib/theme` — this
 * store is just the React handle.
 */

import { useEffect, useState } from "react";
import { create } from "zustand";
import {
  applyTheme,
  getSystemPrefersDark,
  readStoredTheme,
  resolveEffectiveTheme,
  writeStoredTheme,
  type EffectiveTheme,
  type ThemePreference,
} from "@/lib/theme";

interface ThemeState {
  /** Current persisted preference. Defaults to "system" when nothing
   *  is stored — the resolver maps that to the OS color-scheme. */
  pref: ThemePreference;
  setPref: (pref: ThemePreference) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  pref: readStoredTheme() ?? "system",
  setPref: (pref) => {
    writeStoredTheme(pref);
    applyTheme(resolveEffectiveTheme(pref, getSystemPrefersDark()));
    set({ pref });
  },
}));

/**
 * Returns the *effective* theme — what's actually rendered — given
 * the current preference and OS color-scheme. Re-renders subscribers
 * when either source changes:
 *  - `pref` flips via Zustand subscription (Settings UI / Header
 *    button).
 *  - OS pref flips via a `matchMedia` listener (only meaningful when
 *    `pref === "system"`, but cheap to keep mounted unconditionally).
 *
 * Header uses this to draw the right Sun/Moon glyph and to anchor
 * the toggle to the *opposite* of the current effective theme; any
 * future surface that needs the rendered theme (e.g. an SVG that
 * picks a stroke color) can read it the same way.
 */
export function useEffectiveTheme(): EffectiveTheme {
  const pref = useThemeStore((s) => s.pref);
  const [systemDark, setSystemDark] = useState<boolean>(getSystemPrefersDark);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    function handler(e: MediaQueryListEvent) {
      setSystemDark(e.matches);
    }
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return resolveEffectiveTheme(pref, systemDark);
}
